import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import api from "services/api";
import linguistApi from "services/linguistApi";
import {
  startConnectivityMonitor,
  subscribeConnectivity,
} from "services/connectivityMonitor";
import { isLinguistSubdomain } from "utils/subdomainDetector";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Backoff delays between auth-bootstrap retries when the server is
// unreachable (network error / timeout / 5xx). Covers ~15s total, which
// comfortably rides out a server crash + restart.
const AUTH_BOOTSTRAP_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// TRANSIENT = the server never answered (no HTTP response: network error,
// timeout, abort) or answered with a 5xx. The Firebase session is still
// valid in this case — the user must NOT be treated as logged out.
const isTransientAuthError = (error) => {
  if (!error) return false;
  if (!error.response) return true;
  return Number(error.response?.status || 0) >= 500;
};

// EXPLICIT-REJECTION = the server answered and told us the session/account is
// invalid. These keep the existing sign-out behavior exactly as it was.
const isExplicitAuthRejection = (error) => {
  const status = error.response?.status;
  const accountDisabled = error.response?.data?.accountDisabled;
  const userNotFound = error.response?.data?.error === "User not found in database";

  return (status === 403 && accountDisabled) || status === 404 || userNotFound;
};

// Run a request, retrying ONLY transient failures with backoff. Any explicit
// server response (success or 4xx) resolves/rejects immediately.
const requestWithTransientRetry = async (requestFn) => {
  let lastError;

  for (
    let attempt = 0;
    attempt <= AUTH_BOOTSTRAP_RETRY_DELAYS_MS.length;
    attempt += 1
  ) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt === AUTH_BOOTSTRAP_RETRY_DELAYS_MS.length;

      if (!isTransientAuthError(error) || isLastAttempt) break;

      await sleep(AUTH_BOOTSTRAP_RETRY_DELAYS_MS[attempt]);
    }
  }

  throw lastError;
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [linguistUserLoggedIn, setLinguistUserLoggedIn] = useState(false);
  const [serverUnreachable, setServerUnreachable] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSigningOutRef = useRef(false);

  const signOutAndReset = useCallback(async (reasonLog) => {
    if (auth.currentUser && !isSigningOutRef.current) {
      isSigningOutRef.current = true;
      try {
        await signOut(auth);
      } catch (signOutError) {
        console.warn(reasonLog, signOutError);
      } finally {
        isSigningOutRef.current = false;
      }
    }

    setCurrentUser(null);
    setUserLoggedIn(false);
    setLinguistUserLoggedIn(false);
    setServerUnreachable(false);
  }, []);

  const isLinguistRoute = useCallback(() => {
    if (typeof window === "undefined") return false;

    // Host-aware: on the linguist subdomain every route (including /login)
    // belongs to the linguist portal. Elsewhere keep the legacy path-based
    // detection so localhost dev behavior is unchanged.
    if (isLinguistSubdomain()) return true;

    const path = String(window.location.pathname || "").toLowerCase();
    return path.startsWith("/linguist-login") || path.startsWith("/linguist-portal");
  }, []);

  const initializeUser = useCallback(async (user) => {
    if (!user) {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setLinguistUserLoggedIn(false);
      setServerUnreachable(false);
      setLoading(false);
      return;
    }

    // Linguist portal uses dedicated API auth checks and should not be forced
    // through /members/me bootstrap (which can 404 and sign users out).
    if (isLinguistRoute()) {
      try {
        const response = await requestWithTransientRetry(() =>
          linguistApi.get(`/me`),
        );
        setCurrentUser({
          ...user,
          id: response.data?.id,
          email: response.data?.Email || user.email,
          Name: response.data?.Name,
          Image: response.data?.Image ?? null,
          ImageThumbnail: response.data?.ImageThumbnail ?? null,
          Role: "Linguist",
        });
        setUserLoggedIn(false);
        setLinguistUserLoggedIn(true);
        setServerUnreachable(false);
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        const accountDisabled = error?.response?.data?.accountDisabled;
        const userNotFound =
          error?.response?.data?.error === "User not found in database";

        if (status === 403 && accountDisabled) {
          // Real linguist whose account was disabled — keep the existing
          // sign-out intent.
          await signOutAndReset(
            "Could not fully sign out invalid linguist account session:",
          );
        } else if (status === 404 || userNotFound) {
          // This Firebase user simply isn't a linguist (e.g. a member
          // visiting the linguist login page). Probe the member session
          // before touching anything — NEVER sign out a valid member
          // session just because it hit a linguist route.
          try {
            const memberResponse = await api.get("/members/me");
            setCurrentUser({
              ...user,
              id: memberResponse.data.id,
              email: memberResponse.data.Email,
              Role: memberResponse.data.Role,
              Name: memberResponse.data.Name,
              Image: memberResponse.data.Image,
              ImageThumbnail: memberResponse.data.ImageThumbnail,
              HourlyRate: memberResponse.data.HourlyRate,
            });
            setUserLoggedIn(true);
            setLinguistUserLoggedIn(false);
            setServerUnreachable(false);
          } catch (probeError) {
            if (isExplicitAuthRejection(probeError)) {
              // Neither linguist nor member — orphaned session, preserve
              // prior cleanup.
              await signOutAndReset(
                "Could not fully sign out invalid account session:",
              );
            } else {
              // Transient probe failure — do NOT destroy the session.
              setCurrentUser(null);
              setUserLoggedIn(false);
              setLinguistUserLoggedIn(false);
              if (isTransientAuthError(probeError)) {
                setServerUnreachable(true);
                startConnectivityMonitor();
              }
            }
          }
        } else if (isTransientAuthError(error)) {
          // Server unreachable after retries: keep the Firebase user signed
          // in and let the app recover once the server responds again.
          setCurrentUser(user);
          setUserLoggedIn(false);
          setLinguistUserLoggedIn(false);
          setServerUnreachable(true);
          // Show the app-wide reconnecting overlay and keep diagnosing the
          // cause (server down vs. device offline). Also covers cold loads,
          // where the very first requests fire before `NetworkErrorListener`
          // is mounted and the interceptor's notification is a no-op; the
          // monitor is idempotent, so a double start is harmless.
          startConnectivityMonitor();
        } else {
          setCurrentUser(user);
          setUserLoggedIn(false);
          setLinguistUserLoggedIn(false);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = await requestWithTransientRetry(() =>
        api.get(`/members/me`),
      );
      setCurrentUser({
        ...user,
        id: response.data.id,
        email: response.data.Email,
        Role: response.data.Role,
        Name: response.data.Name,
        Image: response.data.Image,
        ImageThumbnail: response.data.ImageThumbnail,
        HourlyRate: response.data.HourlyRate,
      });
      setUserLoggedIn(true);
      setLinguistUserLoggedIn(false);
      setServerUnreachable(false);
    } catch (error) {
      console.error("Auth initialization failed:", error);

      if (isExplicitAuthRejection(error)) {
        await signOutAndReset(
          "Could not fully sign out invalid account session:",
        );
      } else if (isTransientAuthError(error)) {
        // Server unreachable after retries: keep the Firebase session as the
        // source of truth. Do NOT null the user or force the login screen.
        setServerUnreachable(true);
        // Show the app-wide reconnecting overlay and keep diagnosing the
        // cause (server down vs. device offline). Also covers cold loads,
        // where the very first requests fire before `NetworkErrorListener`
        // is mounted and the interceptor's notification is a no-op; the
        // monitor is idempotent, so a double start is harmless.
        startConnectivityMonitor();
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
        setLinguistUserLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  }, [isLinguistRoute, signOutAndReset]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initializeUser);
    return unsubscribe;
  }, [initializeUser]);

  // Re-run the auth bootstrap on demand (e.g. once the connectivity monitor
  // reports the server is back). Safe to call when no Firebase user exists.
  const retryAuthBootstrap = useCallback(async () => {
    await initializeUser(auth.currentUser || null);
  }, [initializeUser]);

  // Recovery bridge: the connectivity monitor owns the "reconnecting" state
  // (and the overlay). When it settles back to idle while this flag is set,
  // finish a bootstrap that failed while the server was down — immediately,
  // instead of waiting for the next 30s poll. Without this, the guards that
  // render nothing until the session hydrates would leave a blank screen
  // behind the (now dismissed) overlay.
  useEffect(() => {
    if (!serverUnreachable) return undefined;

    return subscribeConnectivity((connectivity) => {
      if (connectivity.status !== "idle") return;

      if (auth.currentUser) {
        initializeUser(auth.currentUser).catch((err) => {
          console.warn("Auth recovery re-bootstrap failed:", err);
        });
      } else {
        setServerUnreachable(false);
      }
    });
  }, [serverUnreachable, initializeUser]);

  useEffect(() => {
    if (isLinguistRoute()) return;
    // Poll while a member session is loaded, and also while the server is
    // unreachable so the app recovers on its own once the server is back.
    if (!currentUser?.id && !serverUnreachable) return;

    const intervalId = setInterval(async () => {
      try {
        await api.get(`/members/me`);

        if (!currentUser?.id && auth.currentUser) {
          // The member session was never hydrated (cold boot while the
          // server was down) — finish the bootstrap now that it responds.
          await initializeUser(auth.currentUser);
        } else {
          setServerUnreachable(false);
        }
      } catch (error) {
        const status = error.response?.status;
        const accountDisabled = error.response?.data?.accountDisabled;
        const userNotFound = error.response?.data?.error === "User not found in database";

        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid account session:",
          );
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [
    currentUser,
    serverUnreachable,
    isLinguistRoute,
    signOutAndReset,
    initializeUser,
  ]);

  useEffect(() => {
    if (!isLinguistRoute()) return;
    if (!auth.currentUser) return;
    // Also poll while the server is unreachable so the linguist portal
    // recovers on its own once the server is back.
    if (!linguistUserLoggedIn && !serverUnreachable) return;

    const intervalId = setInterval(async () => {
      try {
        await linguistApi.get(`/me`);

        if (!linguistUserLoggedIn && auth.currentUser) {
          // The linguist session was never hydrated (cold boot while the
          // server was down) — finish the bootstrap now that it responds.
          await initializeUser(auth.currentUser);
        } else {
          setServerUnreachable(false);
        }
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        const accountDisabled = error?.response?.data?.accountDisabled;
        const userNotFound =
          error?.response?.data?.error === "User not found in database";

        // Match member behavior: sign out only for invalid/disabled accounts,
        // do not force logout on transient auth/network failures.
        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid linguist account session:",
          );
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [
    isLinguistRoute,
    signOutAndReset,
    currentUser,
    linguistUserLoggedIn,
    serverUnreachable,
    initializeUser,
  ]);

  const value = {
    currentUser,
    userLoggedIn,
    linguistUserLoggedIn,
    loading,
    serverUnreachable,
    retryAuthBootstrap,
    setCurrentUser,
    setLinguistUserLoggedIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
